import { useRef, useState, type KeyboardEvent } from "react";
import { Expand, Info } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

const BASE = import.meta.env.BASE_URL.replace(/\/?$/, "/");

export const SHOWCASE_PAGES = [
  { id: "cover", label: "Cover", file: "quote-cover.png", alt: "Example quotation cover page" },
  { id: "summary", label: "Quote Summary", file: "quote-summary.png", alt: "Example quotation summary page with totals" },
  { id: "breakdown", label: "Detailed Breakdown", file: "quote-breakdown.png", alt: "Example detailed breakdown of work items and prices" },
  { id: "thank-you", label: "Thank-you Page", file: "quote-thank-you.png", alt: "Example thank-you page closing the quotation" },
] as const;

export function showcaseSrc(file: string) {
  return `${BASE}assets/quote-showcase/${file}`;
}

const DISCLAIMER = "Example only. Prices shown are illustrative and are not a quote or a price promise.";

export function QuoteShowcase() {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const dialogTabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const previewRef = useRef<HTMLButtonElement | null>(null);
  const page = SHOWCASE_PAGES[active];

  const onKey = (e: KeyboardEvent, enlarged = false) => {
    const n = SHOWCASE_PAGES.length;
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (active + 1) % n;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (active - 1 + n) % n;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    if (next < 0) return;
    e.preventDefault();
    setActive(next);
    (enlarged ? dialogTabRefs : tabRefs).current[next]?.focus();
  };

  return (
    <section aria-labelledby="showcase-heading" className="rounded-xl border bg-card p-4 sm:p-5" data-testid="section-quote-showcase">
      <h2 id="showcase-heading" className="font-semibold leading-snug" data-testid="text-showcase-title">
        See what your quotation could look like
      </h2>
      <p className="mt-1 text-sm font-medium text-primary">Professional quotations, ready for your customers.</p>
      <p className="mt-2 text-sm text-muted-foreground">
        From the cover to the detailed breakdown, create a quotation you're proud to send. Customise the design to suit your business.
      </p>

      <div role="tablist" aria-label="Example quotation pages" onKeyDown={onKey} className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {SHOWCASE_PAGES.map((p, i) => {
          const selected = i === active;
          return (
            <button
              key={p.id}
              ref={(el) => { tabRefs.current[i] = el; }}
              type="button"
              role="tab"
              id={`showcase-tab-${p.id}`}
              aria-selected={selected}
              aria-controls="showcase-panel"
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(i)}
              data-testid={`button-showcase-${p.id}`}
              className={`min-h-10 rounded-lg border px-2 py-2 text-xs sm:text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                selected ? "border-primary bg-primary text-primary-foreground" : "bg-background hover:border-primary/50 hover:bg-primary/5"
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <div id="showcase-panel" role="tabpanel" aria-labelledby={`showcase-tab-${page.id}`} className="mt-3">
        <button
          ref={previewRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Enlarge ${page.label} example`}
          data-testid="button-showcase-preview"
          className="group relative flex h-64 sm:h-80 w-full items-center justify-center overflow-hidden rounded-lg border bg-muted/40 p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <img
            src={showcaseSrc(page.file)}
            alt={page.alt}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain"
            data-testid="img-showcase-preview"
          />
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-background/90 px-2 py-1 text-xs font-semibold shadow-sm ring-1 ring-border">
            <Expand className="h-3.5 w-3.5" aria-hidden /> Tap to enlarge
          </span>
        </button>
      </div>

      <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground" data-testid="text-showcase-disclaimer">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        {DISCLAIMER}
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onCloseAutoFocus={event => { event.preventDefault(); previewRef.current?.focus(); }} className="max-h-[92dvh] w-[calc(100vw-1.5rem)] max-w-4xl grid-rows-[auto_auto_minmax(0,1fr)] gap-3 p-4 sm:p-6" data-testid="dialog-showcase">
          <div className="pr-8">
            <DialogTitle data-testid="text-showcase-dialog-title">{page.label} example</DialogTitle>
            <DialogDescription className="mt-1 text-xs">{DISCLAIMER}</DialogDescription>
          </div>
          <div role="tablist" aria-label="Example quotation pages (enlarged)" onKeyDown={event => onKey(event, true)} className="flex flex-wrap gap-1.5">
            {SHOWCASE_PAGES.map((p, i) => (
              <button
                key={p.id}
                ref={element => { dialogTabRefs.current[i] = element; }}
                type="button"
                role="tab"
                id={`showcase-dialog-tab-${p.id}`}
                aria-controls="showcase-dialog-panel"
                aria-selected={i === active}
                tabIndex={i === active ? 0 : -1}
                onClick={() => setActive(i)}
                data-testid={`button-showcase-dialog-${p.id}`}
                className={`rounded-md border px-2.5 py-1.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  i === active ? "border-primary bg-primary text-primary-foreground" : "bg-background hover:bg-primary/5"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div id="showcase-dialog-panel" role="tabpanel" aria-labelledby={`showcase-dialog-tab-${page.id}`} className="min-h-0 overflow-auto rounded-md border bg-muted/40">
            <img src={showcaseSrc(page.file)} alt={page.alt} className="mx-auto h-auto max-h-[70dvh] w-full object-contain" data-testid="img-showcase-enlarged" />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
